import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Check, 
  CreditCard, 
  Lock, 
  ArrowLeft,
  Building,
  User,
  Mail,
  Shield,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const plans = {
  essentials: {
    name: 'Essentials',
    monthlyPrice: 25,
    yearlyPrice: 20,
    features: ['1 user seat', '250 RFQs/year', 'CRM & RFQ intake', 'Basic BOM', 'Email support'],
  },
  professional: {
    name: 'Professional',
    monthlyPrice: 100,
    yearlyPrice: 80,
    features: ['5 user seats', '1,000 RFQs/year', 'BOM versioning', 'Workflow & approvals', 'Analytics', 'Priority support'],
  },
};

export const SubscribePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const planKey = searchParams.get('plan') || 'professional';
  const billing = searchParams.get('billing') || 'annual';
  const plan = plans[planKey] || plans.professional;
  const isAnnual = billing === 'annual';
  const price = isAnnual ? plan.yearlyPrice : plan.monthlyPrice;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: planKey,
          billing: billing,
          origin_url: originUrl,
          metadata: {
            customer_email: formData.email,
            customer_name: `${formData.firstName} ${formData.lastName}`,
            company: formData.company,
            plan: plan.name,
            billing_cycle: billing,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Unable to process payment. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="subscribe-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          {/* <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="back-to-pricing"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Pricing
          </Link> */}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="glass-card p-8 sticky top-32">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-6">
                {/* Plan Details */}
                <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold">{plan.name} Plan</h3>
                      <p className="text-white/50 text-sm">
                        {isAnnual ? 'Annual billing' : 'Monthly billing'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">${price}</div>
                      <div className="text-white/50 text-sm">/month</div>
                    </div>
                  </div>
                  
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">
                      Included:
                    </p>
                    <ul className="space-y-2">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                          <Check className="w-4 h-4 text-[#00D4FF]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between text-white/70">
                    <span>Subtotal</span>
                    <span>${isAnnual ? price * 12 : price}/{ isAnnual ? 'year' : 'month'}</span>
                  </div>
                  {isAnnual && (
                    <div className="flex justify-between text-[#00D4FF]">
                      <span>Annual discount (15%)</span>
                      <span>-${Math.round((plan.monthlyPrice - plan.yearlyPrice) * 12)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>${isAnnual ? price * 12 : price}/{ isAnnual ? 'year' : 'month'}</span>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <Lock className="w-4 h-4" />
                    <span>Secure checkout</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <Shield className="w-4 h-4" />
                    <span>256-bit encryption</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Checkout Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold mb-2">Complete Your Subscription</h2>
              <p className="text-white/50 mb-8">
                You'll be redirected to Stripe for secure payment processing
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="firstName">
                      First Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] outline-none transition-all"
                        placeholder="John"
                        data-testid="input-firstname"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="lastName">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] outline-none transition-all"
                      placeholder="Doe"
                      data-testid="input-lastname"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="email">
                    Work Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] outline-none transition-all"
                      placeholder="john@company.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="company">
                    Company Name
                  </label>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] outline-none transition-all"
                      placeholder="Acme Manufacturing"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#00D4FF] text-[#0A1628] font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-payment"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Proceed to Payment
                    </>
                  )}
                </button>

                {/* Terms */}
                <p className="text-white/40 text-xs text-center">
                  By subscribing, you agree to our{' '}
                  <Link to="/terms" className="text-[#00D4FF] hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-[#00D4FF] hover:underline">Privacy Policy</Link>.
                </p>
              </form>
            </div>

            {/* Powered by Stripe */}
            <div className="mt-6 text-center">
              <p className="text-white/40 text-sm flex items-center justify-center gap-2">
                <Lock className="w-4 h-4" />
                Payments secured by Stripe
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
